import { ApolloError, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useLocation } from 'react-router-dom'
import Cookies from 'universal-cookie/es6/Cookies'
import { v4 } from 'uuid'
import { SubscriptionCreateDocument, SubscriptionCreateInput, SubscriptionStatus, UserProfileDocument } from '../../../graphql/generated'
import useDecodedIdToken from '../../auth/hooks/useDecodedIdToken'
import useGAEvent from '../../auth/hooks/useGAEvent'

interface Props {
  redirectOnSuccess?: boolean
  price?: string
}

const SUCCESS_STATUSES = [SubscriptionStatus.Active, SubscriptionStatus.Pending] as SubscriptionStatus[]

export default function useSubscriptionCreate({ redirectOnSuccess = true, price }: Props = {}) {
  const location = useLocation()
  const [query] = useLazyQuery(UserProfileDocument, { fetchPolicy: 'network-only' })
  const { trackEvent } = useGAEvent()
  const { customClaims } = useDecodedIdToken(true)
  const isPUCUser = customClaims?.isPUCUser ?? false
  const userProfile = useQuery(UserProfileDocument)
  const bucketId = userProfile?.data?.userProfile?.currentBucketId
  const isFreeTier = userProfile?.data?.userProfile?.plan?.plan_name === 'freemium'

  const cookies = new Cookies()

  const [mutation, response] = useMutation(SubscriptionCreateDocument, {
    onCompleted: async (data) => {
      if (!redirectOnSuccess) {
        return
      }

      const status = data?.subscriptions?.create?.subscription?.status ?? SubscriptionStatus.Expired
      if (!SUCCESS_STATUSES.includes(status)) {
        return
      }

      const results = await query()
      trackEvent('purchase', {
        ecommerce: {
          currency: 'USD',
          value: price ?? 0,
          coupon: '',
          transaction_id: results.data?.userProfile?.subscription?.id ?? v4(),
          items: [
            {
              item_id: `${results.data?.userProfile?.subscription?.planName?.replace('-', '_')}`,
              item_name: `${results.data?.userProfile?.subscription?.planName?.replace('-', ' ')}`,
              coupon: '',
              discount: 0,
              item_brand: 'Photobucket',
              quantity: 1,
              amount: price ?? 0,
              index: 0,
              price: price ?? 0,
              item_category: 'recurly'
            }
          ]
        }
      })

      if (isPUCUser || isFreeTier) {
        window.location.href = `/bucket/${bucketId}`
      } else {
        window.location.href = `/subscription/success${location.search}`
      }
    }
  })

  const createSubscription = async (input: SubscriptionCreateInput, handleError?: (apolloError: ApolloError) => void) => {
    try {
      let newInput = input
      const base64Green = cookies.get('pb_green')

      if (base64Green) {
        newInput = { ...input, trackingData: base64Green }
      }
      const result = await mutation({ variables: { input: newInput } })
      return result
    } catch (err) {
      const apolloError = err as ApolloError
      if (handleError) {
        handleError(apolloError)
      }
      return null
    }
  }

  return {
    createSubscription,
    data: response.data,
    error: response.error,
    loading: response.loading,
    called: response.called,
    success: Boolean(response?.data?.subscriptions?.create?.id)
  }
}
